/**
 * Actycrea: loading.esm.js (ES6)
 * MIT
 *
 * Site intro screen: corporate red canvas with the centered isotype. Once the
 * page is ready the mark zooms in, the canvas whitens and the whole overlay
 * cross-fades into the site.
 *
 * The zoom is animated on the SVG `viewBox`, not with `transform: scale()`.
 * A transform blows up the raster the browser produced at layout size, which
 * at x25 reads as a pixelated bitmap; re-framing the viewBox keeps the shape
 * vectorial and every frame is rasterized crisp at viewport resolution.
 * `transform` is kept only as the fallback path for a mark with no usable
 * viewBox.
 *
 * Playback is gated by two conditions at once: the window 'load' event ( or a
 * safety timeout, so a slow third-party asset never holds the visitor hostage )
 * and a minimum on-screen time, which keeps the intro from flashing on warm
 * cache hits.
 *
 * Fires '{PREFIX}LoadingComplete' on window when the overlay is gone, so hero
 * modules can start their own entrance animations on cue.
 */
import { gsap } from 'gsap';
import { PREFIX } from '../helpers/config.js';

const ATTR_ROOT     = `data-${PREFIX}-loading`;
const SELECTOR_DATA = `[${ATTR_ROOT}]`;
const MARK_SELECTOR = '.loading-mark';
const HTML_CLASS    = 'loading';
const ZOOM_CLASS    = 'is-zoom';
const EVENT_DONE    = `${PREFIX}LoadingComplete`;

const DEFAULTS = {
  minDuration    : 400 ,       // ms the intro stays on screen at the very least
  safetyTimeout  : 5000 ,      // ms before playing regardless of window.load
  zoomScale      : 26 ,        // how many times the mark grows
  zoomDuration   : 1 ,         // s
  whitenStart    : 0.55 ,      // s into the timeline
  whitenDuration : 0.35 ,      // s
  fadeStart      : 0.9 ,       // s into the timeline
  fadeDuration   : 0.35 ,      // s
  whitenColor    : '#FFFFFF'
};

export class Loading {
  /**
   * @param {HTMLElement} element
   * @param {Object}      config
   */
  constructor( element , config = { } ){
    if( !( element instanceof HTMLElement ) ) throw new Error( 'Loading: elemento inválido' );

    this._config  = { ...DEFAULTS , ...config };
    this._element = element;
    this._mark    = this._element.querySelector( MARK_SELECTOR );
    this._svg     = this._mark ? this._mark.querySelector( 'svg' ) : null;
    this._done    = false;
    this._reduced = window.matchMedia( '( prefers-reduced-motion: reduce )' ).matches;

    Promise.all( [ this._whenLoaded( ) , this._whenMinDurationElapsed( ) ] ).then( ( ) => this._play( ) );
  }

  /**
   * Resolves on window 'load', or on the safety timeout — whichever lands first.
   *
   * @returns {Promise<void>}
   */
  _whenLoaded( ){
    return new Promise( ( resolve ) => {
      let settled = false;
      const settle = ( ) => {
        if( settled ) return;
        settled = true;
        resolve( );
      };

      if( document.readyState === 'complete' ){
        settle( );
      } else {
        window.addEventListener( 'load' , settle , { once: true } );
      }

      window.setTimeout( settle , this._config.safetyTimeout );
    } );
  }

  /**
   * Resolves once the intro has been visible long enough to read as intentional.
   *
   * @returns {Promise<void>}
   */
  _whenMinDurationElapsed( ){
    return new Promise( ( resolve ) => window.setTimeout( resolve , this._config.minDuration ) );
  }

  /**
   * Reads the mark's own coordinate system and its rendered resting size, and
   * derives everything the viewBox zoom needs from them.
   *
   * `scale` is expressed in screen pixels per viewBox unit: at rest it is
   * whatever SCSS decided the mark should measure, which is why the resting
   * size lives in the stylesheet and not here.
   *
   * @returns {?{ centerX: number , centerY: number , scale: number }}
   */
  _measure( ){
    if( !this._svg ) return null;

    const box = this._svg.viewBox && this._svg.viewBox.baseVal ? this._svg.viewBox.baseVal : null;
    if( !box || !box.width || !box.height ) return null;

    const rect = this._mark.getBoundingClientRect( );
    if( !rect.width ) return null;

    return {
      centerX : box.x + ( box.width / 2 ) ,
      centerY : box.y + ( box.height / 2 ) ,
      scale   : rect.width / box.width
    };
  }

  /**
   * Frames the viewport around the mark's center at a given scale.
   *
   * @param {{ centerX: number , centerY: number }} frame
   * @param {number} scale  Screen pixels per viewBox unit.
   * @returns {void}
   */
  _applyViewBox( frame , scale ){
    const width  = window.innerWidth / scale;
    const height = window.innerHeight / scale;

    this._svg.setAttribute( 'viewBox' , [
      frame.centerX - ( width / 2 ) ,
      frame.centerY - ( height / 2 ) ,
      width ,
      height
    ].join( ' ' ) );
  }

  /**
   * Builds and runs the outro timeline.
   *
   * @returns {void}
   */
  _play( ){
    if( this._done ) return;

    const c  = this._config;
    const tl = gsap.timeline( { onComplete: ( ) => this._finish( ) } );

    if( this._reduced || !this._mark ){
      tl.to( this._element , { autoAlpha: 0 , duration: 0.3 , ease: 'none' } );
      return;
    }

    const frame = this._measure( );

    if( frame ){
      // Vectorial zoom: the mark takes over the viewport and the viewBox
      // tightens around its center, so the shape is re-rasterized every frame.
      this._svg.setAttribute( 'preserveAspectRatio' , 'xMidYMid meet' );
      this._applyViewBox( frame , frame.scale );
      this._element.classList.add( ZOOM_CLASS );

      const zoom = { scale: frame.scale };

      tl.to( zoom , {
        scale    : frame.scale * c.zoomScale ,
        duration : c.zoomDuration ,
        ease     : 'power2.in' ,
        onUpdate : ( ) => this._applyViewBox( frame , zoom.scale )
      } , 0 );
    } else {
      // Fallback for a mark with no usable viewBox: transform scaling. Less
      // sharp, but it never leaves the intro without an animation.
      tl.to( this._mark , {
        scale           : c.zoomScale ,
        transformOrigin : '50% 50%' ,
        duration        : c.zoomDuration ,
        ease            : 'power2.in'
      } , 0 );
    }

    tl.to( this._element , {
      backgroundColor : c.whitenColor ,
      duration        : c.whitenDuration ,
      ease            : 'sine.out'
    } , c.whitenStart )
      .to( this._element , {
        autoAlpha : 0 ,
        duration  : c.fadeDuration ,
        ease      : 'power1.out'
      } , c.fadeStart );
  }

  /**
   * Unlocks the document, drops the overlay from the DOM and notifies listeners.
   *
   * @returns {void}
   */
  _finish( ){
    if( this._done ) return;
    this._done = true;

    document.documentElement.classList.remove( HTML_CLASS );
    this._element.remove( );
    window.dispatchEvent( new CustomEvent( EVENT_DONE ) );
  }

  /**
   * Force-closes the intro ( escape hatch for debugging or editor previews ).
   *
   * @returns {void}
   */
  destroy( ){
    gsap.killTweensOf( [ this._element , this._mark ] );
    this._finish( );
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ){
    const nodes     = root.querySelectorAll( SELECTOR_DATA );
    const instances = [ ];

    // No overlay on the page ( partial not rendered, no mark configured ):
    // drop the state class so nothing downstream waits on an intro that
    // will never play.
    if( !nodes.length ){
      document.documentElement.classList.remove( HTML_CLASS );
      return instances;
    }

    nodes.forEach( ( el ) => {
      if( el._loadingInstance ) return;
      el._loadingInstance = new Loading( el );
      instances.push( el._loadingInstance );
    } );
    return instances;
  }
}

export default Loading;
